import foodMenuSeed from '../data/foodMenu.json'
import type { FoodItem, MenuOption } from '../types'
import { diffRecordFields } from '@shared/lib/orderAuditDiff'
import { appendAuditLog } from './auditLog'
import { dispatchCatalogEvent, MENU_UPDATED_EVENT } from './catalogEvents'
import { useFirestore } from './firebase'
import * as firestoreMenuItems from './firestore/menuItems'
import {
  getCategories,
  UNCATEGORIZED_CATEGORY_ID,
  updateCategory,
} from './categoryStorage'
import { effectiveMenuCategoryId } from './menuItemReorder'
import {
  assignPerCategorySortIndices,
  sortMenuItemsByCategoryOrder,
} from './menuSortOrder'
import {
  POS_EXTRA_ITEM_ID,
  applyPosExtraLock,
  catalogExtraNeedsRelock,
  defaultPosExtraItemFields,
  extraUncategorizedSortIndex,
  isPosExtraItem,
  lockPosExtraFields,
} from '@shared/lib/posExtraItem'
import {
  applyStockConsumeToItem,
  mergeSoldOutFromPrevious,
  soldOutRowCount,
  withNormalizedSoldOut,
} from '@shared/lib/soldOutItem'
import type { SoldOutItemState } from './firestore/menuItems'

const STORAGE_KEY = 'wortelogy-menu'
const seedData = foodMenuSeed as FoodItem[]

function normalizeOption(opt: MenuOption): MenuOption {
  const choices = opt.choices ?? []
  const prices = opt.choicePrices ?? []
  const choicePrices = choices.map((_, i) => (Array.isArray(prices) && Number.isFinite(prices[i]) ? prices[i]! : 0))
  return { ...opt, choices, choicePrices }
}

function normalizeOptions(
  opts: FoodItem['options'],
  itemId: string,
): MenuOption[] {
  if (!Array.isArray(opts)) return []
  if (opts.length === 0) return []
  const first = opts[0]
  if (
    typeof first === 'object' &&
    first !== null &&
    'id' in first &&
    'type' in first
  ) {
    return (opts as MenuOption[]).map(normalizeOption)
  }
  return (opts as string[]).map((label, i) =>
    normalizeOption({
      id: `legacy-${itemId}-${i}-${label}`,
      label,
      type: 'checklist' as const,
      choices: [label],
    }),
  )
}

function normalizeItem(item: FoodItem): FoodItem {
  const options = normalizeOptions(item.options, item.id)
  const sortIndex =
    typeof item.sortIndex === 'number' && Number.isFinite(item.sortIndex)
      ? item.sortIndex
      : undefined
  return withNormalizedSoldOut({
    ...item,
    options,
    sortIndex,
    ...(item.posOnly === true ? { posOnly: true } : {}),
  })
}

/** Raw list from localStorage (no sort) — avoids recursion when pairing with getCategories. */
function readFoodItemsLocalUnsorted(): FoodItem[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) {
      seedFromJson()
      return readFoodItemsLocalUnsorted()
    }
    const parsed = JSON.parse(raw) as FoodItem[]
    return parsed.map(normalizeItem)
  } catch {
    return []
  }
}

function ensureLocalPosExtra(items: FoodItem[]): FoodItem[] {
  const existing = items.find(isPosExtraItem)
  if (existing) {
    if (!catalogExtraNeedsRelock(existing)) return items
    const next = items.map(applyPosExtraLock)
    saveFoodItemsLocal(next)
    dispatchCatalogEvent(MENU_UPDATED_EVENT)
    return next
  }
  const extra: FoodItem = normalizeItem({
    id: POS_EXTRA_ITEM_ID,
    ...defaultPosExtraItemFields(),
    sortIndex: extraUncategorizedSortIndex(items),
  })
  const next = [...items, extra]
  saveFoodItemsLocal(next)
  dispatchCatalogEvent(MENU_UPDATED_EVENT)
  return next
}

function getFoodItemsLocal(): FoodItem[] {
  const list = ensureLocalPosExtra(readFoodItemsLocalUnsorted())
  return sortMenuItemsByCategoryOrder(list, getCategories())
}

function seedFromJson(): void {
  const existing = localStorage.getItem(STORAGE_KEY)
  if (existing) return
  localStorage.setItem(STORAGE_KEY, JSON.stringify(seedData))
}

function saveFoodItemsLocal(items: FoodItem[]): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(items))
}

function generateFoodId(): string {
  return `F-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
}

export function getFoodItems(): FoodItem[] {
  if (useFirestore()) {
    // Only use place-scoped Firestore cache; never fall back to global localStorage
    // to avoid leaking another account/place's menu.
    return firestoreMenuItems.getFoodItems()
  }
  return getFoodItemsLocal()
}

export function saveFoodItems(items: FoodItem[]): void {
  if (useFirestore()) {
    firestoreMenuItems.saveFoodItems(items)
    return
  }
  const previous = readFoodItemsLocalUnsorted()
  const prevById = new Map(previous.map((i) => [i.id, i]))
  const merged = items.map((item) =>
    mergeSoldOutFromPrevious(normalizeItem(item), prevById.get(item.id)),
  )
  const normalized = ensureLocalPosExtra(merged)
  const ordered = sortMenuItemsByCategoryOrder(normalized, getCategories())
  const withSort = assignPerCategorySortIndices(ordered)
  saveFoodItemsLocal(withSort)
}

/** When using Firestore, returns a Promise so the caller can await the write. */
export function addFoodItem(item: Omit<FoodItem, 'id'>): FoodItem | Promise<FoodItem> {
  if (useFirestore()) {
    return firestoreMenuItems.addFoodItem(item).then((full) => {
      appendAuditLog({
        domain: 'menu',
        action: 'menu_item.created',
        message: `Created menu item ${full.name}`,
        entityType: 'menuItem',
        entityId: full.id,
        meta: { name: full.name },
      })
      return full
    })
  }
  const id = generateFoodId()
  const list = readFoodItemsLocalUnsorted()
  const catKey = item.categoryId ?? UNCATEGORIZED_CATEGORY_ID
  const inCat = list.filter(
    (i) => (i.categoryId ?? UNCATEGORIZED_CATEGORY_ID) === catKey,
  )
  const maxIdx = inCat.reduce((m, i) => Math.max(m, i.sortIndex ?? -1), -1)
  const full: FoodItem = normalizeItem({
    ...item,
    id,
    sortIndex: maxIdx + 1,
  })
  saveFoodItems([...list, full])
  appendAuditLog({
    domain: 'menu',
    action: 'menu_item.created',
    message: `Created menu item ${full.name}`,
    entityType: 'menuItem',
    entityId: full.id,
    meta: { name: full.name },
  })
  return full
}

export function updateFoodItem(
  id: string,
  patch: Partial<FoodItem>,
): FoodItem[] {
  const before = getFoodItems().find((i) => i.id === id)
  const lockedPatch =
    before && isPosExtraItem(before)
      ? {
          ...patch,
          ...lockPosExtraFields(),
          categoryId: undefined,
          station: undefined,
        }
      : patch
  const updated = useFirestore()
    ? firestoreMenuItems.updateFoodItem(id, lockedPatch)
    : (() => {
        const items = readFoodItemsLocalUnsorted()
        const next = items.map((i) =>
          i.id === id ? applyPosExtraLock({ ...i, ...lockedPatch }) : i,
        )
        saveFoodItems(next)
        return getFoodItemsLocal()
      })()
  const after = updated.find((i) => i.id === id)
  const keys = Object.keys(lockedPatch)
  // Real before -> after values: a price edit from 50000 to 5000 must be readable.
  const changes = diffRecordFields(
    before as unknown as Record<string, unknown>,
    after as unknown as Record<string, unknown>,
    keys,
  )
  const priceChange = changes.find((c) => c.field === 'price')
  appendAuditLog({
    domain: 'menu',
    action: 'menu_item.updated',
    message: `Updated menu item ${after?.name ?? before?.name ?? id} (${keys.join(', ')})`,
    entityType: 'menuItem',
    entityId: id,
    changes,
    ...(typeof priceChange?.from === 'number' && typeof priceChange.to === 'number'
      ? { amountDelta: priceChange.to - priceChange.from }
      : {}),
    meta: { name: after?.name ?? before?.name ?? '', keys: keys.join(', ') },
  })
  return updated
}

export async function setFoodItemsSoldOut(
  states: ReadonlyMap<string, SoldOutItemState>,
): Promise<FoodItem[]> {
  const before = getFoodItems()
  const updated = useFirestore()
    ? await firestoreMenuItems.setFoodItemsSoldOut(states)
    : (() => {
        const next = readFoodItemsLocalUnsorted().map((item) => {
          if (isPosExtraItem(item)) return applyPosExtraLock(normalizeItem(item))
          const state = states.get(item.id)
          return normalizeItem({
            ...item,
            soldOut: state?.soldOut === true,
            soldOutChoices: state?.soldOutChoices ?? [],
            stockRemaining: state?.stockRemaining,
            soldOutReason: state?.soldOutReason,
          })
        })
        saveFoodItemsLocal(
          assignPerCategorySortIndices(
            sortMenuItemsByCategoryOrder(ensureLocalPosExtra(next), getCategories()),
          ),
        )
        dispatchCatalogEvent(MENU_UPDATED_EVENT)
        return getFoodItemsLocal()
      })()
  const count = updated
    .filter((i) => !isPosExtraItem(i))
    .reduce((sum, item) => sum + soldOutRowCount(item), 0)
  appendAuditLog({
    domain: 'menu',
    action: 'menu_item.updated',
    message: `Updated sold-out items (${count})`,
    entityType: 'menuItem',
    entityId: 'sold-out-set',
    meta: { count, beforeCount: before.reduce((sum, item) => sum + soldOutRowCount(item), 0) },
  })
  return updated
}

export function applyItemStockDeltas(deltas: ReadonlyMap<string, number>): void {
  if (useFirestore()) {
    firestoreMenuItems.applyItemStockDeltas(deltas)
    return
  }
  if (deltas.size === 0) return
  const next = readFoodItemsLocalUnsorted().map((item) => {
    const consume = deltas.get(item.id) ?? 0
    if (consume === 0 || typeof item.stockRemaining !== 'number') return item
    return applyPosExtraLock(normalizeItem(applyStockConsumeToItem(item, consume)))
  })
  saveFoodItemsLocal(
    assignPerCategorySortIndices(
      sortMenuItemsByCategoryOrder(ensureLocalPosExtra(next), getCategories()),
    ),
  )
  dispatchCatalogEvent(MENU_UPDATED_EVENT)
}

export function deleteFoodItem(id: string): FoodItem[] {
  const before = getFoodItems().find((i) => i.id === id)
  if (before && isPosExtraItem(before)) return getFoodItems()
  const updated = useFirestore()
    ? firestoreMenuItems.deleteFoodItem(id)
    : (() => {
        const items = readFoodItemsLocalUnsorted().filter((i) => i.id !== id)
        saveFoodItems(items)
        return getFoodItemsLocal()
      })()
  appendAuditLog({
    domain: 'menu',
    action: 'menu_item.deleted',
    message: `Deleted menu item ${before?.name ?? id}`,
    entityType: 'menuItem',
    entityId: id,
    meta: { name: before?.name ?? '' },
  })
  return updated
}

export function clearCategoryFromItems(categoryId: string): void {
  if (useFirestore()) {
    firestoreMenuItems.clearCategoryFromItems(categoryId)
    return
  }
  const items = readFoodItemsLocalUnsorted()
  const updated = items.map((i) =>
    i.categoryId === categoryId ? { ...i, categoryId: undefined } : i,
  )
  saveFoodItems(updated)
}

export function clearStationFromItems(stationName: string): void {
  if (useFirestore()) {
    firestoreMenuItems.clearStationFromItems(stationName)
    return
  }
  const items = readFoodItemsLocalUnsorted()
  const updated = items.map((i) =>
    i.station === stationName ? { ...i, station: undefined } : i,
  )
  saveFoodItems(updated)
}

export function replaceStationNameInItems(
  oldName: string,
  newName: string,
): void {
  if (useFirestore()) {
    firestoreMenuItems.replaceStationNameInItems(oldName, newName)
    return
  }
  const items = readFoodItemsLocalUnsorted()
  const updated = items.map((i) =>
    i.station === oldName ? { ...i, station: newName } : i,
  )
  saveFoodItems(updated)
}

export function applyCategoryStationToItems(
  categoryId: string,
  station?: string,
): void {
  if (useFirestore()) {
    firestoreMenuItems.applyCategoryStationToItems(categoryId, station)
    return
  }
  const categories = getCategories()
  const validCategoryIds = new Set(categories.map((c) => c.id))
  const items = readFoodItemsLocalUnsorted()
  const trimmed = station?.trim()
  const updated = items.map((i) => {
    if (effectiveMenuCategoryId(i, validCategoryIds) !== categoryId) return i
    if (trimmed) return { ...i, station: trimmed }
    const { station: _s, ...rest } = i
    return rest as FoodItem
  })
  saveFoodItems(updated)
}

export function setCategoryStation(categoryId: string, station?: string): void {
  const trimmed = station?.trim()
  updateCategory(categoryId, { station: trimmed })
  applyCategoryStationToItems(categoryId, trimmed)
}
